import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import WidthContainer from '../WidthContainer';
import { BodyMedium, ParagraphSmallText, TitleLarge } from '../Texts';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useDispatch, useSelector } from 'react-redux';
import { selectPrivyToken } from '../../redux/slices/auth';
import { selectThemeMode } from '../../redux/slices/themeSlice';
import { useGetCurrentUserProfileQuery } from '../../redux/api/authorized/auth';
import { usePrivy } from '@privy-io/react-auth';
import { openModal } from '../../redux/slices/modals';

const Container = styled.div`
  padding-top: 120px;
  padding-bottom: 120px;
  display: flex;
  flex-direction: column;
  position: relative;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    width: 100%;
    padding-top: 80px;
    padding-bottom: 80px;
  }
`;

const ContainerLayerTwo = styled.div`
  display: flex;
  gap: 20%;
  align-items: center;
  flex: 1;
  justify-content: space-between;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    align-items: flex-start;
    gap: 30px;
  }
`;

const ContainerLayerThree = styled.div`
  display: flex;
  gap: 20%;
  text-wrap: nowrap;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    gap: 30px;
    justify-content: center;
    align-items: center;
    width: 100%;
    text-align: center;
  }
`;

const UnderlineContainer = styled.div`
  gap: 30px;
  display: flex;
  flex-direction: row;
  color: ${({ theme }) => theme.text.secondary};
  bottom: 20px;
  width: 100%;
  padding-top: 30px;
  padding-bottom: 30px;
  justify-content: center;
  border-top: 1px solid ${({ theme }) => theme.border.secondary};
  @media ${PHONE_MEDIA_QUERY} {
    bottom: unset;
    padding-top: 30px;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
`;

const InnerContainerLayer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
  @media ${PHONE_MEDIA_QUERY} {
    align-items: center;
    width: 100%;
    text-align: center;
  }
`;

const Spacer = styled.div`
  height: 100px;
  ${() =>
    Capacitor.isNativePlatform() &&
    `
      height: calc(70px + env(safe-area-inset-bottom));
    `}
`;

const Footer = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const themeMode = useSelector(selectThemeMode);
  const dispatch = useDispatch();
  const privyToken = useSelector(selectPrivyToken);
  const { user } = usePrivy();
  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const theme = useTheme();

  if (Capacitor.isNativePlatform() || (isMobile && user)) {
    return <Spacer />;
  }

  return (
    <WidthContainer>
      <Container>
        <ContainerLayerTwo>
          <ContainerLayerThree>
            <InnerContainerLayer>
              <img
                style={{
                  width: '60%',
                  maxWidth: '150px',
                }}
                src={
                  themeMode === 'light'
                    ? '/logo-footer.webp'
                    : '/logo-footer-dark.webp'
                }
                alt='Deks Logo'
              />
              <div>
                <ParagraphSmallText style={{ marginTop: 16 }}>
                  521 5th Avenue 17th Floor,
                </ParagraphSmallText>
                <ParagraphSmallText>#9033 New York</ParagraphSmallText>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                <a href='https://x.com/DEKSxyz' target='_blank'>
                  <img src='/twitter.svg' />
                </a>
                <a href='https://www.instagram.com/deks.xyz/' target='_blank'>
                  <img src='/instagram.svg' />
                </a>
                <a href='https://t.me/deksofficial' target='_blank'>
                  <img src='/telegram.svg' />
                </a>
              </div>
            </InnerContainerLayer>
          </ContainerLayerThree>
          <ContainerLayerThree>
            <InnerContainerLayer>
              <TitleLarge style={{ color: theme.text.title }}>{t('footer.info')}</TitleLarge>
              <div>
                <ParagraphSmallText style={{ marginTop: 16 }}>
                  <a
                    style={{
                      textDecoration: 'underline',
                      color: theme.text.regular,
                    }}
                    href='https://deks.xyz/terms'
                    target='_blank'
                  >
                    {t('footer.termsOfUse')}
                  </a>
                </ParagraphSmallText>
                <ParagraphSmallText>
                  <a
                    style={{
                      textDecoration: 'underline',
                      color: theme.text.regular,
                    }}
                    href='https://deks.xyz/privacy'
                    target='_blank'
                  >
                    {t('footer.privacyPolicy')}
                  </a>
                </ParagraphSmallText>
              </div>
              <ParagraphSmallText
                onClick={() => {
                  dispatch(openModal({ name: 'downloadApp' }));
                }}
                style={{
                  textDecoration: 'underline',
                  color: theme.text.regular,
                  cursor: 'pointer',
                }}
              >
                {t('footer.downloadOurApp')}
              </ParagraphSmallText>
            </InnerContainerLayer>
          </ContainerLayerThree>
          <ContainerLayerThree>
            <InnerContainerLayer>
              <TitleLarge style={{ color: theme.text.title }}>
                {t('footer.contacts')}
              </TitleLarge>
              <div>
                <ParagraphSmallText style={{ marginTop: 16 }}>
                  {t('footer.email')}:{' '}
                  <a
                    style={{
                      textDecoration: 'underline',
                      color: theme.text.regular,
                    }}
                    href='mailto:info@deks.xyz'
                  >
                    info@deks.xyz
                  </a>
                </ParagraphSmallText>
              </div>
            </InnerContainerLayer>
          </ContainerLayerThree>
        </ContainerLayerTwo>
      </Container>
      <UnderlineContainer>
        <BodyMedium>{t('footer.copyright')}</BodyMedium>
      </UnderlineContainer>
    </WidthContainer>
  );
};

export default Footer;
